// components/screener/ResultsTable.jsx
import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
    Box, Tabs, Tab, Typography, Stack, Select, MenuItem, FormControl,
    IconButton, Chip, Popover, Checkbox, Button, InputBase,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    NavigateBefore as PrevIcon,
    NavigateNext as NextIcon,
    TuneRounded as TuneIcon,
    SearchRounded as SearchIcon,
    RestartAltRounded as ResetIcon,
} from '@mui/icons-material';
import { VIEWS, COLUMNS, shortNumber, COLUMN_GROUPS, DEFAULT_CUSTOM_COLUMNS } from './screenerConfig';
import { formatNumber } from '../../utils/numberUtils';
import currencySymbols from '../../utils/currencySymbols';

const STORAGE_KEY = 'screener_custom_columns';

const loadCustomColumns = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        }
    } catch (e) { /* ignore */ }
    return DEFAULT_CUSTOM_COLUMNS;
};

const saveCustomColumns = (cols) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cols));
    } catch (e) { /* ignore */ }
};

const ResultsTable = ({
    results,
    currentView,
    onViewChange,
    page,
    pageSize,
    totalResults,
    totalPages,
    onPageChange,
    onPageSizeChange,
    isLoading,
}) => {
    const theme = useTheme();
    const baseUrl = window.location.origin;

    // Custom column state
    const [customColumns, setCustomColumns] = useState(loadCustomColumns);
    const [pickerAnchor, setPickerAnchor] = useState(null);
    const [pickerSearch, setPickerSearch] = useState('');
    const searchInputRef = useRef(null);

    // Save to localStorage when custom columns change
    useEffect(() => {
        saveCustomColumns(customColumns);
    }, [customColumns]);

    // Focus search input when popover opens
    useEffect(() => {
        if (pickerAnchor && searchInputRef.current) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [pickerAnchor]);

    const toggleColumn = useCallback((colId) => {
        if (colId === 'symbol') return; // locked
        setCustomColumns(prev => {
            if (prev.includes(colId)) {
                return prev.filter(id => id !== colId);
            }
            return [...prev, colId];
        });
    }, []);

    const toggleGroupAll = useCallback((groupColumns) => {
        setCustomColumns(prev => {
            const allSelected = groupColumns.every(c => prev.includes(c));
            if (allSelected) {
                // Remove all group columns (except symbol)
                return prev.filter(id => !groupColumns.includes(id) || id === 'symbol');
            }
            // Add all missing group columns
            const toAdd = groupColumns.filter(c => !prev.includes(c));
            return [...prev, ...toAdd];
        });
    }, []);

    const resetCustomColumns = useCallback(() => {
        setCustomColumns(DEFAULT_CUSTOM_COLUMNS);
    }, []);

    // Find current view config — override columns for 'all' (Custom) view
    const viewConfig = useMemo(() => {
        const view = VIEWS.find(v => v.id === currentView) || VIEWS[0];
        if (view.id === 'all') {
            return { ...view, columns: customColumns };
        }
        return view;
    }, [currentView, customColumns]);

    // Build DataGrid columns from config
    const columns = useMemo(() => {
        return viewConfig.columns.map(colId => {
            const colDef = COLUMNS[colId];
            if (!colDef) return null;

            const col = {
                field: colDef.field,
                headerName: colDef.headerName,
                minWidth: colDef.minWidth || 100,
                flex: colDef.flex || 0.7,
                sortable: true,
            };

            // Apply render type
            switch (colDef.renderType) {
                case 'company':
                    col.renderCell = (params) => (
                        <Box>
                            <RouterLink
                                to={`${baseUrl}/asset/${params.row.symbol}`}
                                style={{
                                    color: theme.palette.primary.main,
                                    textDecoration: 'none',
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                }}
                            >
                                {params.row.symbol}
                            </RouterLink>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    color: theme.palette.text.secondary,
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    lineHeight: 1.2,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 160,
                                }}
                            >
                                {params.row.name || params.row.companyName || ''}
                            </Typography>
                        </Box>
                    );
                    break;

                case 'currency':
                    col.renderCell = (params) => {
                        const sym = currencySymbols[params.row.currency] || '$';
                        return (
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.88rem' }}>
                                {sym}{formatNumber(params.value)}
                            </Typography>
                        );
                    };
                    col.valueFormatter = (params) => formatNumber(params.value);
                    break;

                case 'changePercent':
                    col.renderCell = (params) => {
                        const val = params.value;
                        const color = val > 0 ? theme.palette.positive.main
                            : val < 0 ? theme.palette.negative.main
                                : theme.palette.text.secondary;
                        return (
                            <Typography variant="body2" sx={{ color, fontWeight: 600, fontSize: '0.88rem' }}>
                                {val > 0 ? '+' : ''}{formatNumber(val)}%
                            </Typography>
                        );
                    };
                    break;

                case 'change':
                    col.renderCell = (params) => {
                        const val = params.value;
                        const color = val > 0 ? theme.palette.positive.main
                            : val < 0 ? theme.palette.negative.main
                                : theme.palette.text.secondary;
                        return (
                            <Typography variant="body2" sx={{ color, fontWeight: 500, fontSize: '0.88rem' }}>
                                {val > 0 ? '+' : ''}{formatNumber(val)}
                            </Typography>
                        );
                    };
                    break;

                case 'shortNumber':
                    col.renderCell = (params) => (
                        <Typography variant="body2" sx={{ fontSize: '0.88rem' }}>
                            {params.value != null ? shortNumber(params.value) : '—'}
                        </Typography>
                    );
                    col.valueFormatter = (params) => params.value != null ? shortNumber(params.value) : '';
                    break;

                case 'number':
                    col.valueFormatter = (params) => params.value != null ? formatNumber(params.value) : '';
                    break;

                case 'percent':
                    col.renderCell = (params) => {
                        if (params.value == null) return '—';
                        const pct = (params.value * 100).toFixed(2);
                        return (
                            <Typography variant="body2" sx={{ fontSize: '0.88rem' }}>
                                {pct}%
                            </Typography>
                        );
                    };
                    col.valueFormatter = (params) => params.value != null ? `${(params.value * 100).toFixed(2)}%` : '';
                    break;

                case 'date':
                    col.valueFormatter = (params) =>
                        params.value ? new Date(params.value).toLocaleDateString() : '';
                    break;

                case 'consensus':
                    col.renderCell = (params) => {
                        if (!params.value) return '—';
                        const colorMap = {
                            'Strong Buy': theme.palette.positive.main,
                            'Buy': '#4caf50',
                            'Hold': theme.palette.text.secondary,
                            'Sell': '#ff9800',
                            'Strong Sell': theme.palette.negative.main,
                        };
                        return (
                            <Chip
                                label={params.value}
                                size="small"
                                sx={{
                                    fontWeight: 600,
                                    fontSize: '0.72rem',
                                    color: '#fff',
                                    bgcolor: colorMap[params.value] || theme.palette.text.secondary,
                                    height: 24,
                                }}
                            />
                        );
                    };
                    break;

                default:
                    break;
            }

            return col;
        }).filter(Boolean);
    }, [viewConfig, theme, baseUrl]);

    // Tab index
    const tabIndex = VIEWS.findIndex(v => v.id === currentView);

    const startRow = (page - 1) * pageSize + 1;
    const endRow = Math.min(page * pageSize, totalResults);

    // Filtered column groups for picker search
    const filteredGroups = useMemo(() => {
        if (!pickerSearch.trim()) return COLUMN_GROUPS;
        const q = pickerSearch.toLowerCase();
        return COLUMN_GROUPS.map(group => ({
            ...group,
            columns: group.columns.filter(colId => {
                const col = COLUMNS[colId];
                return col && (
                    col.headerName.toLowerCase().includes(q) ||
                    col.field.toLowerCase().includes(q)
                );
            }),
        })).filter(group => group.columns.length > 0);
    }, [pickerSearch]);

    return (
        <Box sx={{ mt: 2 }}>
            {/* View tabs */}
            <Box sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <Tabs
                    value={tabIndex >= 0 ? tabIndex : 0}
                    onChange={(_, newIndex) => onViewChange(VIEWS[newIndex].id)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 40,
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            minHeight: 40,
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            color: theme.palette.text.secondary,
                            px: 2,
                            '&.Mui-selected': {
                                color: theme.palette.primary.main,
                                fontWeight: 700,
                            },
                        },
                        '& .MuiTabs-indicator': {
                            height: 2.5,
                            borderRadius: 2,
                        },
                    }}
                >
                    {VIEWS.map((view) => (
                        <Tab
                            key={view.id}
                            label={
                                view.id === 'all' ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {view.label}
                                        {currentView === 'all' && (
                                            <TuneIcon
                                                sx={{
                                                    fontSize: 16,
                                                    color: theme.palette.primary.main,
                                                    ml: 0.25,
                                                }}
                                            />
                                        )}
                                    </Box>
                                ) : view.label
                            }
                        />
                    ))}
                </Tabs>

                {/* Column picker button — only visible on Custom tab */}
                {currentView === 'all' && (
                    <IconButton
                        size="small"
                        onClick={(e) => setPickerAnchor(e.currentTarget)}
                        sx={{
                            mr: 1,
                            color: theme.palette.primary.main,
                            border: `1px solid ${theme.palette.primary.main}40`,
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 0.5,
                            '&:hover': {
                                bgcolor: `${theme.palette.primary.main}10`,
                            },
                        }}
                    >
                        <TuneIcon sx={{ fontSize: 18, mr: 0.5 }} />
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            Columns ({customColumns.length - 1})
                        </Typography>
                    </IconButton>
                )}
            </Box>

            {/* Column picker popover */}
            <Popover
                open={Boolean(pickerAnchor)}
                anchorEl={pickerAnchor}
                onClose={() => { setPickerAnchor(null); setPickerSearch(''); }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            width: 380,
                            maxHeight: 520,
                            bgcolor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            boxShadow: theme.shadows[8],
                        }
                    }
                }}
            >
                {/* Picker header */}
                <Box sx={{
                    p: 1.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                }}>
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 0.5,
                    }}>
                        <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
                        <InputBase
                            inputRef={searchInputRef}
                            placeholder="Search columns..."
                            value={pickerSearch}
                            onChange={(e) => setPickerSearch(e.target.value)}
                            sx={{
                                flex: 1,
                                fontSize: '0.82rem',
                                '& input': { p: 0 },
                            }}
                        />
                    </Box>
                    <Button
                        size="small"
                        startIcon={<ResetIcon sx={{ fontSize: 16 }} />}
                        onClick={resetCustomColumns}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            minWidth: 'auto',
                            px: 1,
                            '&:hover': { color: theme.palette.primary.main },
                        }}
                    >
                        Reset
                    </Button>
                </Box>

                {/* Column groups */}
                <Box sx={{ overflow: 'auto', maxHeight: 440, p: 1 }}>
                    {filteredGroups.map((group) => {
                        const allSelected = group.columns.every(c => customColumns.includes(c));
                        const someSelected = group.columns.some(c => customColumns.includes(c));

                        return (
                            <Box key={group.label} sx={{ mb: 1 }}>
                                {/* Group header with select-all */}
                                <Box
                                    onClick={() => toggleGroupAll(group.columns)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        py: 0.5,
                                        px: 0.5,
                                        cursor: 'pointer',
                                        borderRadius: 1,
                                        '&:hover': {
                                            bgcolor: theme.palette.mode === 'dark'
                                                ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                        },
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={allSelected}
                                        indeterminate={someSelected && !allSelected}
                                        sx={{
                                            p: 0.25,
                                            '& .MuiSvgIcon-root': { fontSize: 18 },
                                        }}
                                    />
                                    <Typography variant="caption" sx={{
                                        fontSize: '0.72rem',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: theme.palette.text.secondary,
                                    }}>
                                        {group.label}
                                    </Typography>
                                </Box>

                                {/* Column checkboxes */}
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.25,
                                    pl: 1,
                                }}>
                                    {group.columns.map(colId => {
                                        const col = COLUMNS[colId];
                                        if (!col) return null;
                                        const isSelected = customColumns.includes(colId);

                                        return (
                                            <Box
                                                key={colId}
                                                onClick={() => toggleColumn(colId)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.25,
                                                    py: 0.25,
                                                    px: 0.75,
                                                    cursor: 'pointer',
                                                    borderRadius: 1,
                                                    minWidth: '45%',
                                                    '&:hover': {
                                                        bgcolor: theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                                    },
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={isSelected}
                                                    sx={{
                                                        p: 0.25,
                                                        '& .MuiSvgIcon-root': { fontSize: 16 },
                                                    }}
                                                />
                                                <Typography variant="body2" sx={{
                                                    fontSize: '0.78rem',
                                                    fontWeight: isSelected ? 600 : 400,
                                                    color: isSelected
                                                        ? theme.palette.text.primary
                                                        : theme.palette.text.secondary,
                                                }}>
                                                    {col.headerName}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        );
                    })}

                    {filteredGroups.length === 0 && (
                        <Typography variant="body2" sx={{
                            textAlign: 'center',
                            py: 3,
                            color: theme.palette.text.secondary,
                            fontSize: '0.82rem',
                        }}>
                            No columns match "{pickerSearch}"
                        </Typography>
                    )}
                </Box>
            </Popover>

            {/* DataGrid */}
            <Box sx={{ width: '100%', mt: 1 }}>
                <DataGrid
                    rows={results}
                    columns={columns}
                    getRowId={(row) => row.symbol}
                    loading={isLoading}
                    disableColumnMenu
                    hideFooter
                    autoHeight
                    rowHeight={52}
                    columnHeaderHeight={44}
                    disableRowSelectionOnClick
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: theme.palette.mode === 'dark'
                                ? 'rgba(19, 47, 76, 0.5)' : 'rgba(0,0,0,0.02)',
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            color: theme.palette.text.secondary,
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            whiteSpace: 'normal',
                            lineHeight: 1.2,
                            overflow: 'visible',
                            [theme.breakpoints.up('md')]: {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            },
                        },
                        '& .MuiDataGrid-cell': {
                            borderColor: theme.palette.divider,
                            fontSize: '0.88rem',
                            py: 0.5,
                        },
                        '& .MuiDataGrid-row': {
                            transition: 'background 0.15s',
                            '&:hover': {
                                bgcolor: theme.palette.mode === 'dark'
                                    ? 'rgba(0,127,255,0.04)' : 'rgba(0,127,255,0.02)',
                            },
                        },
                        '& .MuiDataGrid-overlay': {
                            bgcolor: 'transparent',
                        },
                    }}
                />
            </Box>

            {/* Custom pagination bar */}
            {totalResults > 0 && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1,
                    mt: 2,
                    py: 1.5,
                    px: 1,
                    borderTop: `1px solid ${theme.palette.divider}`,
                }}>
                    {/* Left: showing x-y of z */}
                    <Typography variant="body2" sx={{
                        fontSize: '0.82rem',
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                    }}>
                        Showing <strong>{startRow}</strong>–<strong>{endRow}</strong> of{' '}
                        <strong>{totalResults.toLocaleString()}</strong>
                    </Typography>

                    {/* Center: page controls */}
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <IconButton
                            size="small"
                            disabled={page <= 1 || isLoading}
                            onClick={() => onPageChange(page - 1)}
                            sx={{ color: theme.palette.text.primary }}
                        >
                            <PrevIcon />
                        </IconButton>

                        <Typography variant="body2" sx={{
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            px: 1.5,
                        }}>
                            {page} / {totalPages}
                        </Typography>

                        <IconButton
                            size="small"
                            disabled={page >= totalPages || isLoading}
                            onClick={() => onPageChange(page + 1)}
                            sx={{ color: theme.palette.text.primary }}
                        >
                            <NextIcon />
                        </IconButton>
                    </Stack>

                    {/* Right: page size */}
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: theme.palette.text.secondary }}>
                            Per page:
                        </Typography>
                        <FormControl size="small">
                            <Select
                                value={pageSize}
                                onChange={(e) => onPageSizeChange(e.target.value)}
                                sx={{
                                    fontSize: '0.82rem',
                                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                                }}
                            >
                                {[10, 25, 50].map(size => (
                                    <MenuItem key={size} value={size} sx={{ fontSize: '0.85rem' }}>{size}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default ResultsTable;